import { z } from 'zod';
/* ───────── Auth ───────── */
export const LoginInputSchema = z.object({
    email: z.string().trim().toLowerCase().email('Email inválido'),
    senha: z.string().min(1, 'Informe a senha'),
});
export const SessionUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    nome: z.string(),
    role: z.enum(['ADMIN', 'VIEWER']),
});
export const LoginResponseSchema = z.object({
    user: SessionUserSchema,
    accessToken: z.string(),
});
/* ───────── Class ───────── */
export const ClassStatusSchema = z.enum(['DRAFT', 'TESTING', 'PRODUCTION']);
export const SLUG_REGEX = /^[a-z0-9][a-z0-9-]*$/;
export const ClassSchema = z.object({
    id: z.string().uuid(),
    slug: z.string().min(2).max(64).regex(SLUG_REGEX),
    name: z.string().min(2).max(120),
    definition: z.string().max(8000).default(''),
    status: ClassStatusSchema,
    version: z.number().int().nonnegative(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const ClassCreateInputSchema = z.object({
    name: z.string().trim().min(2, 'Nome muito curto').max(120, 'Nome muito longo'),
    definition: z.string().max(8000).default(''),
});
export const ClassUpdateInputSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    definition: z.string().max(8000).optional(),
    status: ClassStatusSchema.optional(),
});
/* ───────── Signal (sinais estruturados pré-LLM) ───────── */
export const SignalKindSchema = z.enum(['EXCERPT', 'KEYWORD_POS', 'KEYWORD_NEG', 'LEGAL_ARTICLE']);
export const ClassSignalSchema = z.object({
    id: z.string().uuid(),
    classId: z.string().uuid(),
    kind: SignalKindSchema,
    content: z.string().min(1).max(2000),
    position: z.number().int().nonnegative(),
    createdAt: z.coerce.date(),
});
export const ClassSignalCreateInputSchema = z.object({
    kind: SignalKindSchema,
    content: z.string().trim().min(1, 'Conteúdo obrigatório').max(2000, 'Conteúdo muito longo'),
});
/* Bulk replace — envia todos os sinais de cada tipo de uma vez (UX do wizard) */
export const ClassSignalsBulkInputSchema = z.object({
    excerpts: z.array(z.string().trim().min(1).max(2000)).max(50).default([]),
    keywordsPos: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    keywordsNeg: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    legalArticles: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
});
/* Conflict detection — quando o mesmo termo aparece em outras classes */
export const SignalConflictMatchSchema = z.object({
    classId: z.string().uuid(),
    className: z.string(),
    classSlug: z.string(),
    signalKind: SignalKindSchema,
});
export const SignalConflictItemSchema = z.object({
    kind: SignalKindSchema,
    content: z.string(),
    conflictsWith: z.array(SignalConflictMatchSchema).min(1),
});
export const SignalConflictReportSchema = z.object({
    conflicts: z.array(SignalConflictItemSchema),
});
/* ───────── Gold examples ───────── */
export const ClassGoldExampleSchema = z.object({
    id: z.string().uuid(),
    classId: z.string().uuid(),
    text: z.string().min(1),
    externalId: z.string().nullable(),
    cnj: z.string().nullable(),
    createdAt: z.coerce.date(),
});
export const ClassGoldExampleCreateInputSchema = z.object({
    text: z
        .string()
        .trim()
        .min(20, 'Cole a publicação completa (mín. 20 chars)')
        .max(100_000, 'Texto excedeu o limite de 100k chars'),
    externalId: z.string().trim().max(120).optional(),
    cnj: z.string().trim().max(40).optional(),
});
export const MAX_GOLD_EXAMPLES = 10;
/* ───────── Regex Rule ───────── */
export const RegexFlagsSchema = z.string().regex(/^[gimsuy]*$/, 'Flags válidas: g i m s u y');
export const GeneratedBySchema = z.enum(['LLM', 'MANUAL']);
export const RegexRuleSchema = z.object({
    id: z.string().uuid(),
    classId: z.string().uuid(),
    generationId: z.string().uuid().nullable(),
    name: z.string().min(2).max(160),
    pattern: z.string().min(1).max(4000),
    flags: RegexFlagsSchema,
    weight: z.number().int().min(1).max(100),
    enabled: z.boolean(),
    generatedBy: GeneratedBySchema,
    explanation: z.string().nullable(),
    goldHits: z.number().int().nonnegative(),
    goldTotal: z.number().int().nonnegative(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const RegexRuleUpdateInputSchema = z.object({
    name: z.string().trim().min(2).max(160).optional(),
    pattern: z.string().trim().min(1).max(4000).optional(),
    flags: RegexFlagsSchema.optional(),
    weight: z.number().int().min(1).max(100).optional(),
    enabled: z.boolean().optional(),
});
export const RegexRuleCreateInputSchema = z.object({
    name: z.string().trim().min(2, 'Nome muito curto').max(160),
    pattern: z.string().trim().min(1, 'Padrão obrigatório').max(4000),
    flags: RegexFlagsSchema.default('i'),
    weight: z.number().int().min(1).max(100).default(10),
    enabled: z.boolean().default(true),
});
/* ───────── Regex Generation (LLM run snapshot) ───────── */
export const GenerationStatusSchema = z.enum(['RUNNING', 'COMPLETED', 'FAILED']);
export const CoverageReportSchema = z.object({
    goldTotal: z.number().int().nonnegative(),
    rulesCovered: z.number().int().nonnegative(),
    perRule: z.array(z.object({
        ruleName: z.string(),
        hits: z.number().int().nonnegative(),
        missedExampleIndexes: z.array(z.number().int().nonnegative()),
    })),
    uncoveredExampleIndexes: z.array(z.number().int().nonnegative()),
});
export const RegexGenerationSchema = z.object({
    id: z.string().uuid(),
    classId: z.string().uuid(),
    status: GenerationStatusSchema,
    refinedDefinition: z.string().nullable(),
    coverageReport: CoverageReportSchema.nullable(),
    llmModel: z.string().nullable(),
    llmTokensInput: z.number().int().nullable(),
    llmTokensOutput: z.number().int().nullable(),
    llmCostUsd: z.union([z.string(), z.number()]).nullable(),
    errorMessage: z.string().nullable(),
    createdAt: z.coerce.date(),
    finishedAt: z.coerce.date().nullable(),
});
/* Output JSON shape that the LLM must respect */
export const LlmRegexRuleSchema = z.object({
    name: z.string().min(2).max(160),
    pattern: z.string().min(1).max(4000),
    flags: RegexFlagsSchema,
    explanation: z.string().min(1).max(2000),
});
export const LlmGenerationOutputSchema = z.object({
    rules: z.array(LlmRegexRuleSchema).min(1).max(15),
    refinedDefinition: z.string().min(1).max(8000),
});
/* Aggregate response sent back from generate-regex endpoint */
export const ClassWithDetailsSchema = z.object({
    class: ClassSchema,
    signals: z.array(ClassSignalSchema),
    goldExamples: z.array(ClassGoldExampleSchema),
    rules: z.array(RegexRuleSchema),
    latestGeneration: RegexGenerationSchema.nullable(),
});
/* ───────── Test sets / benchmarks ───────── */
export const TestRunStatusSchema = z.enum(['RUNNING', 'COMPLETED', 'FAILED']);
export const TestSetRowSchema = z.object({
    id: z.string().uuid(),
    testSetId: z.string().uuid(),
    externalId: z.string(),
    cnj: z.string().nullable(),
    text: z.string(),
    expectedClassRaw: z.string(),
    expectedClassId: z.string().uuid().nullable(),
    createdAt: z.coerce.date(),
});
export const TestSetSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    sourceFilename: z.string().nullable(),
    totalRows: z.number().int().nonnegative(),
    classLabels: z.array(z.string()),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const TestSetListItemSchema = TestSetSchema.extend({
    runsCount: z.number().int().nonnegative(),
    lastRunAt: z.coerce.date().nullable(),
    lastRunStatus: TestRunStatusSchema.nullable(),
    lastRunPrecision: z.number().nullable(),
    lastRunCoverage: z.number().nullable(),
});
export const ConfidenceTierSchema = z.enum(['ALTA', 'MEDIA', 'BAIXA']);
export const FpBreakdownEntrySchema = z.object({
    classId: z.string().uuid().nullable(),
    className: z.string(),
    count: z.number().int().nonnegative(),
});
export const PerRuleMetricSchema = z.object({
    ruleId: z.string().uuid(),
    ruleName: z.string(),
    classId: z.string().uuid(),
    className: z.string(),
    weight: z.number().int(),
    matches: z.number().int().nonnegative(),
    correctMatches: z.number().int().nonnegative(),
    falsePositives: z.number().int().nonnegative(),
    classTotal: z.number().int().nonnegative(),
    precision: z.number(),
    coverage: z.number(),
    confidence: ConfidenceTierSchema,
    fpBreakdown: z.array(FpBreakdownEntrySchema),
});
export const PerClassMetricSchema = z.object({
    classId: z.string().uuid().nullable(),
    className: z.string(),
    total: z.number().int().nonnegative(),
    correctlyPredicted: z.number().int().nonnegative(),
    misclassified: z.number().int().nonnegative(),
    notClassified: z.number().int().nonnegative(),
    predictedCount: z.number().int().nonnegative(),
    precision: z.number(),
    recall: z.number(),
    f1: z.number(),
});
export const ConfusionCellSchema = z.object({
    expectedClassId: z.string().uuid().nullable(),
    expectedClassName: z.string(),
    predictedClassId: z.string().uuid().nullable(),
    predictedClassName: z.string(),
    count: z.number().int().nonnegative(),
});
export const TestRunMetricsSchema = z.object({
    totalRows: z.number().int().nonnegative(),
    classified: z.number().int().nonnegative(),
    classifiedCorrect: z.number().int().nonnegative(),
    notClassified: z.number().int().nonnegative(),
    precisionGlobal: z.number(),
    coverageGlobal: z.number(),
    llmRate: z.number(),
    conflictRows: z.number().int().nonnegative(),
    conflictRate: z.number(),
    perClass: z.array(PerClassMetricSchema),
    perRule: z.array(PerRuleMetricSchema),
    confusion: z.array(ConfusionCellSchema),
});
export const TestRunSchema = z.object({
    id: z.string().uuid(),
    testSetId: z.string().uuid(),
    status: TestRunStatusSchema,
    metrics: TestRunMetricsSchema.nullable(),
    llmInsights: z.string().nullable(),
    llmModel: z.string().nullable(),
    llmTokensInput: z.number().int().nullable(),
    llmTokensOutput: z.number().int().nullable(),
    llmCostUsd: z.union([z.string(), z.number()]).nullable(),
    errorMessage: z.string().nullable(),
    startedAt: z.coerce.date(),
    finishedAt: z.coerce.date().nullable(),
});
export const TestSetDetailSchema = z.object({
    testSet: TestSetSchema,
    runs: z.array(TestRunSchema),
});
export const PredictionRowSchema = z.object({
    id: z.string().uuid(),
    rowId: z.string().uuid(),
    externalId: z.string(),
    cnj: z.string().nullable(),
    text: z.string(),
    expectedClassRaw: z.string(),
    expectedClassId: z.string().uuid().nullable(),
    expectedClassName: z.string().nullable(),
    predictedClassId: z.string().uuid().nullable(),
    predictedClassName: z.string().nullable(),
    matchedRuleIds: z.array(z.string()),
    allMatchedClassIds: z.array(z.string()),
    correct: z.boolean(),
});
export const PredictionsPageSchema = z.object({
    total: z.number().int().nonnegative(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(200),
    items: z.array(PredictionRowSchema),
});
export const PredictionFilterSchema = z.enum([
    'ALL',
    'CORRECT',
    'INCORRECT',
    'NOT_CLASSIFIED',
    // FP = previu uma classe e errou; FN = era de classe cadastrada e não foi
    // corretamente identificada (errou a classe ou não classificou).
    'FALSE_POSITIVE',
    'FALSE_NEGATIVE',
]);
export const TestSetUploadResponseSchema = z.object({
    testSet: TestSetSchema,
    totalRowsImported: z.number().int().nonnegative(),
    rowsSkipped: z.number().int().nonnegative(),
    classMatches: z.array(z.object({
        label: z.string(),
        classId: z.string().uuid().nullable(),
        className: z.string().nullable(),
        rowCount: z.number().int().nonnegative(),
    })),
});
export const SPREADSHEET_REQUIRED_COLUMNS = ['ID_EXTERNO', 'PROCESSO', 'TEXTO', 'CLASSE'];
/* ───────── Classification (preview Sprint 2) ───────── */
export const MatchedBySchema = z.enum(['REGEX', 'LLM', 'NONE']);
/* ───────── Erros ───────── */
export const ApiErrorSchema = z.object({
    statusCode: z.number().int(),
    message: z.string(),
    code: z.string().optional(),
    fieldErrors: z.record(z.array(z.string())).optional(),
});
//# sourceMappingURL=index.js.map